import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { transitions } from '../lib/motion';
import { useI18n } from '../i18n';
import { api, type Access, type Grant, type Ledger } from '../lib/api';
import { useLedgerContext } from '../lib/ledger-context';
import { useAccessInvalidation, useCreateLedger, useGroups, useShare } from '../lib/queries';
import { accentVar, cn } from '../lib/ui';
import { IconCheck, IconChevronDown, IconPlus, IconUser, IconUsers } from './icons';
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  Field,
  Input,
  Select,
  Sheet,
  Textarea,
  useToast,
} from './ui';

const ACCESS_LEVELS: Access[] = ['view', 'contribute', 'manage'];

function accessLabel(access: Access, t: ReturnType<typeof useI18n>['t']): string {
  return t(`access.${access}` as 'access.view');
}

// --- Switcher ---------------------------------------------------------------

/**
 * The ledger switcher.
 *
 * Hidden entirely for anyone with exactly one ledger — most people — so the
 * concept never has to be explained to someone who does not need it. It only
 * appears once there is genuinely a choice to make.
 */
export function LedgerSwitcher() {
  const { t } = useI18n();
  const { ledgers, current, select, isLoading } = useLedgerContext();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (isLoading || ledgers.length <= 1) return null;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        // Reads as a control on any backdrop. Once the header collapses it sits
        // on translucent, blurred white — a white pill with a 9%-black hairline
        // dissolved into it, which is what "blurred by the top margin" was.
        className="press flex max-w-[220px] items-center gap-2 rounded-full border border-[var(--line-strong)] bg-[var(--surface)] py-1.5 pl-2.5 pr-2 text-[13px] font-medium text-[var(--ink)] shadow-[0_1px_2px_rgba(0,0,0,0.07)] hover:bg-[var(--surface-sunken)]"
      >
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ background: current ? accentVar(current.color) : 'var(--ink-muted)' }}
          aria-hidden="true"
        />
        <span className="truncate">{current ? current.name : t('ledger.all')}</span>
        <IconChevronDown size={15} className="shrink-0 text-[var(--ink-muted)]" />
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={transitions.pop}
            role="listbox"
            // Opaque rather than translucent: this floats directly over body
            // copy, where a blur is decoration and legibility is not.
            className="absolute right-0 z-50 mt-2 w-[260px] origin-top-right overflow-hidden rounded-[var(--radius-card)] border border-[var(--line)] bg-[var(--surface)] shadow-[var(--shadow-pop)]"
          >
            <button
              type="button"
              role="option"
              aria-selected={!current}
              onClick={() => {
                select(null);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-[var(--surface-sunken)]"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--surface-sunken)] text-[var(--ink-muted)]">
                <IconUsers size={14} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] text-[var(--ink)]">{t('ledger.all')}</span>
                <span className="block text-[12px] text-[var(--ink-secondary)]">
                  {t('ledger.allHint')}
                </span>
              </span>
              {!current ? <IconCheck size={16} className="text-[var(--accent)]" /> : null}
            </button>

            <div className="h-px bg-[var(--line)]" />

            {ledgers.map((ledger) => (
              <button
                key={ledger.id}
                type="button"
                role="option"
                aria-selected={current?.id === ledger.id}
                onClick={() => {
                  select(ledger.id);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-[var(--surface-sunken)]"
              >
                <span
                  className="h-6 w-6 shrink-0 rounded-full"
                  style={{ background: accentVar(ledger.color) }}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] text-[var(--ink)]">
                    {ledger.name}
                  </span>
                  <span className="block text-[12px] text-[var(--ink-secondary)]">
                    {t('ledger.receiptCount', { count: ledger.receiptCount })}
                  </span>
                </span>
                {current?.id === ledger.id ? (
                  <IconCheck size={16} className="text-[var(--accent)]" />
                ) : null}
              </button>
            ))}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

// --- Create -----------------------------------------------------------------

export function CreateLedgerSheet({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (id: string) => void;
}) {
  const { t, currency } = useI18n();
  const toast = useToast();
  const create = useCreateLedger();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [openedOn, setOpenedOn] = useState('');

  const submit = () => {
    create.mutate(
      {
        name: name.trim(),
        description: description.trim() || null,
        currency,
        hsaEstablishedDate: openedOn || null,
      },
      {
        onSuccess: ({ id }) => {
          toast.show(t('common.saved'), 'positive');
          setName('');
          setDescription('');
          setOpenedOn('');
          onCreated?.(id);
          onClose();
        },
      },
    );
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('ledger.createTitle')}
      footer={
        <Button
          fullWidth
          size="lg"
          disabled={!name.trim()}
          loading={create.isPending}
          onClick={submit}
        >
          {t('ledger.create')}
        </Button>
      }
    >
      <p className="mb-4 text-[14px] leading-relaxed text-[var(--ink-secondary)]">
        {t('ledger.createBody')}
      </p>

      <div className="space-y-4">
        <Field label={t('ledger.name')}>
          <Input
            autoFocus
            value={name}
            placeholder={t('ledger.namePlaceholder')}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Field label={t('ledger.description')} optional>
          <Textarea
            rows={2}
            value={description}
            placeholder={t('ledger.descriptionPlaceholder')}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        <Field label={t('ledger.opened')} hint={t('ledger.openedHelp')} optional>
          <Input
            type="date"
            value={openedOn}
            onChange={(event) => setOpenedOn(event.target.value)}
          />
        </Field>
      </div>
    </Sheet>
  );
}

// --- Sharing ----------------------------------------------------------------

function AccessSelect({
  value,
  onChange,
  disabled,
}: {
  value: Access;
  onChange: (access: Access) => void;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  return (
    <Select
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value as Access)}
      className="w-auto py-1.5 text-[13px]"
      aria-label={t('access.level')}
    >
      {ACCESS_LEVELS.map((level) => (
        <option key={level} value={level}>
          {accessLabel(level, t)}
        </option>
      ))}
    </Select>
  );
}

function GrantRow({
  grant,
  ledgerId,
  isSelf,
  onChanged,
}: {
  grant: Grant;
  ledgerId: string;
  isSelf: boolean;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [confirmRemove, setConfirmRemove] = useState(false);

  const change = async (access: Access) => {
    try {
      await api.changeAccess(ledgerId, grant.id, access);
      onChanged();
    } catch {
      toast.show(t('access.lastManager'), 'critical');
    }
  };

  const remove = async () => {
    try {
      await api.revokeAccess(ledgerId, grant.id);
      onChanged();
      setConfirmRemove(false);
    } catch {
      toast.show(t('access.lastManager'), 'critical');
      setConfirmRemove(false);
    }
  };

  return (
    <>
      {/*
        Avatar, name, an access menu as wide as "Can add receipts", and a Remove
        button do not share one line on a phone: the menu alone is 179px, which
        left 34px for the name. The controls move to their own line until there
        is room for them beside it.
      */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5">
        {grant.groupId ? (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--surface-sunken)] text-[var(--ink-secondary)]">
            <IconUsers size={17} />
          </span>
        ) : (
          <Avatar name={grant.name} color={grant.avatarColor} size={36} />
        )}

        <div className="min-w-0 flex-1 basis-[55%]">
          {/* min-w-0 on the row as well as the name: without it the flex item
              refuses to shrink below its content and `truncate` never engages,
              so a long name pushed the badge off the sheet. */}
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="min-w-0 truncate text-[14px] text-[var(--ink)]">{grant.name}</span>
            {isSelf ? (
              <span className="shrink-0">
                <Badge>{t('access.you')}</Badge>
              </span>
            ) : null}
          </div>
          <div className="truncate text-[12px] text-[var(--ink-secondary)]">
            {grant.groupId
              ? t('access.groupMembers', { count: grant.memberCount ?? 0 })
              : grant.detail}
          </div>
        </div>

        <div className="flex min-w-0 items-center gap-1 max-sm:w-full">
          <AccessSelect value={grant.access} onChange={(access) => void change(access)} />

          <button
            type="button"
            onClick={() => setConfirmRemove(true)}
            aria-label={t('access.remove')}
            className="press shrink-0 rounded-full px-2 py-1 text-[12px] text-[var(--ink-muted)] hover:bg-[var(--surface-sunken)] hover:text-[var(--critical)]"
          >
            {t('common.remove')}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRemove}
        title={t('access.removeConfirmTitle', { name: grant.name })}
        body={t('access.removeConfirmBody')}
        confirmLabel={t('access.remove')}
        destructive
        onCancel={() => setConfirmRemove(false)}
        onConfirm={() => void remove()}
      />
    </>
  );
}

/**
 * The share sheet — the same shape as sharing a document anywhere else: a list
 * of who already has access with a dropdown each, and a picker to add more.
 * Familiar on purpose; nobody should have to learn a new mental model to say
 * "let the bookkeeper see this one".
 */
export function ShareSheet({
  open,
  onClose,
  ledger,
  currentUserId,
}: {
  open: boolean;
  onClose: () => void;
  ledger: Ledger;
  currentUserId: string;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const invalidate = useAccessInvalidation();
  const share = useShare(ledger.id);
  const groups = useGroups();

  const [grants, setGrants] = useState<Grant[]>([]);
  const [candidates, setCandidates] = useState<{
    users: { id: string; name: string; email: string; avatarColor: string }[];
    groups: { id: string; name: string; memberCount: number }[];
  }>({ users: [], groups: [] });
  const [search, setSearch] = useState('');
  const [pendingAccess, setPendingAccess] = useState<Access>('view');

  const refresh = useMemo(
    () => async () => {
      const [grantResult, shareable] = await Promise.all([
        api.grants(ledger.id),
        api.shareable(ledger.id),
      ]);
      setGrants(grantResult.grants);
      setCandidates(shareable);
      invalidate();
    },
    [ledger.id, invalidate],
  );

  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  const needle = search.trim().toLowerCase();
  const matchingUsers = candidates.users.filter(
    (user) =>
      !needle ||
      user.name.toLowerCase().includes(needle) ||
      user.email.toLowerCase().includes(needle),
  );
  const matchingGroups = candidates.groups.filter(
    (group) => !needle || group.name.toLowerCase().includes(needle),
  );

  const add = (body: { userId?: string; groupId?: string }) => {
    share.mutate(
      { ...body, access: pendingAccess },
      {
        onSuccess: () => {
          setSearch('');
          void refresh();
        },
        onError: () => toast.show(t('errors.generic'), 'critical'),
      },
    );
  };

  const nothingToAdd = candidates.users.length === 0 && candidates.groups.length === 0;

  return (
    <Sheet open={open} onClose={onClose} title={ledger.name} size="lg">
      <p className="mb-5 text-[14px] leading-relaxed text-[var(--ink-secondary)]">
        {t('access.shareBody')}
      </p>

      <section className="mb-6">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h3 className="text-[13px] font-semibold text-[var(--ink-secondary)]">
            {t('access.addPeople')}
          </h3>
          <AccessSelect value={pendingAccess} onChange={setPendingAccess} />
        </div>

        {nothingToAdd ? (
          <p className="rounded-[var(--radius-control)] bg-[var(--surface-sunken)] px-3.5 py-3 text-[13px] text-[var(--ink-secondary)]">
            {t('access.nobodyLeft')}
          </p>
        ) : (
          <>
            <Input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('access.searchPlaceholder')}
            />

            <div className="mt-2 max-h-[240px] overflow-y-auto rounded-[var(--radius-control)] border border-[var(--line)]">
              {matchingGroups.length === 0 && matchingUsers.length === 0 ? (
                <p className="px-3.5 py-3 text-[13px] text-[var(--ink-secondary)]">
                  {t('access.noMatches')}
                </p>
              ) : null}

              {matchingGroups.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => add({ groupId: group.id })}
                  className="flex w-full items-center gap-3 px-3.5 py-2.5 text-left hover:bg-[var(--surface-sunken)]"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--surface-sunken)] text-[var(--ink-secondary)]">
                    <IconUsers size={16} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] text-[var(--ink)]">
                      {group.name}
                    </span>
                    <span className="block text-[12px] text-[var(--ink-secondary)]">
                      {t('groups.memberCount', { count: group.memberCount })}
                    </span>
                  </span>
                  <IconPlus size={16} className="shrink-0 text-[var(--ink-muted)]" />
                </button>
              ))}

              {matchingUsers.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => add({ userId: user.id })}
                  className="flex w-full items-center gap-3 px-3.5 py-2.5 text-left hover:bg-[var(--surface-sunken)]"
                >
                  <Avatar name={user.name} color={user.avatarColor} size={32} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] text-[var(--ink)]">
                      {user.name}
                    </span>
                    <span className="block truncate text-[12px] text-[var(--ink-secondary)]">
                      {user.email}
                    </span>
                  </span>
                  <IconPlus size={16} className="shrink-0 text-[var(--ink-muted)]" />
                </button>
              ))}
            </div>

            <p className="mt-2 text-[12px] leading-snug text-[var(--ink-muted)]">
              {t(`access.${pendingAccess}Help` as 'access.viewHelp')}
            </p>
          </>
        )}
      </section>

      <section>
        <h3 className="mb-1 text-[13px] font-semibold text-[var(--ink-secondary)]">
          {t('access.title')}
        </h3>
        <div className="divide-y divide-[var(--line)]">
          {grants.map((grant) => (
            <GrantRow
              key={grant.id}
              grant={grant}
              ledgerId={ledger.id}
              isSelf={grant.userId === currentUserId}
              onChanged={() => void refresh()}
            />
          ))}
        </div>
        {grants.length === 1 ? (
          <p className="mt-3 text-[12px] text-[var(--ink-muted)]">{t('access.onlyYou')}</p>
        ) : null}
      </section>

      {groups.data && groups.data.groups.length === 0 ? (
        <p className="mt-5 rounded-[var(--radius-control)] bg-[var(--surface-sunken)] px-3.5 py-3 text-[12px] leading-snug text-[var(--ink-secondary)]">
          <IconUser size={13} className="mr-1.5 inline align-[-2px]" />
          {t('groups.emptyHelp')}
        </p>
      ) : null}
    </Sheet>
  );
}

/** Compact summary of who a ledger is shared with, for a settings row. */
export function SharedWithSummary({ count }: { count: number }) {
  const { t } = useI18n();
  return (
    <span
      className={cn(
        'text-[13px]',
        count > 1 ? 'text-[var(--ink-secondary)]' : 'text-[var(--ink-muted)]',
      )}
    >
      {count > 1 ? t('access.sharedWith', { count: count - 1 }) : t('access.onlyYou')}
    </span>
  );
}
